import {useBreakpointDown, useBreakpointUp} from '@travel-frontend/core';
import {
  LabelXSmall,
  Stack,
  SPACING_XSMALL,
} from '@travel-frontend/ui-components';
import {Trans, useTranslation} from 'react-i18next';

import {SegmentedList} from '@/components/SegmentedList/SegmentedList';
import {
  TravelTypes,
  type TravelType,
} from '@/modules/common/context/QueryParams/models';
import {mapChildrenAgesToString} from '@/utils/mapChildrenAgesToString';

type Props = {
  stays?: number;
  overnightStays?: number;
  roomType: string;
  boardType: string;
  adultCount: number;
  childrenAges: Array<number>;
  shouldShowChildrenAge: boolean;
  travelType: TravelType;
  small?: boolean;
  isHotelBox2Split3Enabled?: boolean;
};

export const HotelOfferInfo = ({
  adultCount,
  boardType,
  childrenAges,
  overnightStays,
  roomType,
  shouldShowChildrenAge,
  stays,
  travelType,
  small = false,
  isHotelBox2Split3Enabled,
}: Props) => {
  const {t} = useTranslation(['general']);
  const smDown = useBreakpointDown('sm');
  const lgUp = useBreakpointUp('lg');
  const alignment = lgUp && !small ? 'right' : 'left';
  const labelPackageTour = t('general:infoForm.packageTour');
  const labelOwnArrival = t('general:infoForm.ownArrival');

  const items: Array<{key: string; element: React.ReactNode}> = [];

  if (stays !== undefined) {
    items.push({
      key: 'stays',
      element: (
        <LabelXSmall color="contentSecondary">
          {t('general:xDays', {count: stays})}
        </LabelXSmall>
      ),
    });
  }

  if (overnightStays !== undefined && isHotelBox2Split3Enabled === false) {
    items.push({
      key: 'overnightStays',
      element: (
        <LabelXSmall color="contentSecondary">
          {t('general:xNights', {count: overnightStays})}
        </LabelXSmall>
      ),
    });
  }

  const HotelOfferInfoOriginal = (
    <Stack spacing={SPACING_XSMALL}>
      {items.length > 0 && (
        <SegmentedList
          items={items}
          category="secondary"
          variant="solid"
          alignment={alignment}
          columnSpacing="lists/small"
        />
      )}
      <SegmentedList
        items={[
          {
            key: 'roomtype',
            element: (
              <LabelXSmall color="contentSecondary">{roomType}</LabelXSmall>
            ),
          },
          {
            key: 'boardtype',
            element: (
              <LabelXSmall color="contentSecondary">{boardType}</LabelXSmall>
            ),
          },
        ]}
        category="secondary"
        variant="solid"
        alignment={alignment}
        columnSpacing="4px"
      />
      <SegmentedList
        items={[
          {
            key: 'travellers',
            element: (
              <LabelXSmall key="travellers" color="contentSecondary">
                <Trans
                  ns="general"
                  i18nKey="formatTravellers.ALL"
                  components={{
                    nowrap: <span />,
                  }}
                  values={{
                    context: shouldShowChildrenAge
                      ? 'withChildAge'
                      : 'withoutChildAge',
                    adultCount,
                    childrenCount: childrenAges.length,
                    childrenAges: mapChildrenAgesToString(childrenAges),
                  }}
                  shouldUnescape
                />
              </LabelXSmall>
            ),
          },
          {
            key: 'flightInfo',
            element: (
              <LabelXSmall key="flightinfo" color="contentSecondary">
                {travelType === TravelTypes.packageTour
                  ? labelPackageTour
                  : labelOwnArrival}
              </LabelXSmall>
            ),
          },
        ]}
        category="secondary"
        variant="solid"
        alignment={alignment}
        columnSpacing="4px"
      />
    </Stack>
  );

  const HotelOfferInfo2Split3 = (
    <Stack spacing={SPACING_XSMALL}>
      {items.length > 0 && (
        <SegmentedList
          items={[
            items[0],
            {
              key: 'flightInfo',
              element: (
                <LabelXSmall key="flightinfo" color="contentSecondary">
                  {travelType === TravelTypes.packageTour
                    ? labelPackageTour
                    : labelOwnArrival}
                </LabelXSmall>
              ),
            },
          ]}
          category="secondary"
          variant="solid"
          alignment={alignment}
          columnSpacing="4px"
        />
      )}
      <SegmentedList
        items={[
          {
            key: 'travellers',
            element: (
              <LabelXSmall key="travellers" color="contentSecondary">
                <Trans
                  ns="general"
                  i18nKey="formatTravellers.ALL"
                  components={{
                    nowrap: <span />,
                  }}
                  values={{
                    context: shouldShowChildrenAge
                      ? 'withChildAge'
                      : 'withoutChildAge',
                    adultCount,
                    childrenCount: childrenAges.length,
                    childrenAges: mapChildrenAgesToString(childrenAges),
                  }}
                  shouldUnescape
                />
              </LabelXSmall>
            ),
          },
        ]}
        category="secondary"
        variant="solid"
        alignment={alignment}
        columnSpacing="4px"
      />
      <SegmentedList
        items={[
          {
            key: 'roomtype',
            element: (
              <LabelXSmall color="contentSecondary">{roomType}</LabelXSmall>
            ),
          },
          {
            key: 'boardtype',
            element: (
              <LabelXSmall color="contentSecondary">{boardType}</LabelXSmall>
            ),
          },
        ]}
        category="secondary"
        variant="solid"
        alignment={alignment}
        columnSpacing="4px"
      />
    </Stack>
  );

  const HotelOfferInfo2Split3ForTablet = (
    <Stack spacing={SPACING_XSMALL}>
      {items.length > 0 && (
        <SegmentedList
          items={[
            items[0],
            {
              key: 'flightInfo',
              element: (
                <LabelXSmall key="flightinfo" color="contentSecondary">
                  {travelType === TravelTypes.packageTour
                    ? labelPackageTour
                    : labelOwnArrival}
                </LabelXSmall>
              ),
            },
            {
              key: 'travellers',
              element: (
                <LabelXSmall key="travellers" color="contentSecondary">
                  <Trans
                    ns="general"
                    i18nKey="formatTravellers.ALL"
                    components={{
                      nowrap: <span />,
                    }}
                    values={{
                      context: shouldShowChildrenAge
                        ? 'withChildAge'
                        : 'withoutChildAge',
                      adultCount,
                      childrenCount: childrenAges.length,
                      childrenAges: mapChildrenAgesToString(childrenAges),
                    }}
                    shouldUnescape
                  />
                </LabelXSmall>
              ),
            },
          ]}
          category="secondary"
          variant="solid"
          alignment={alignment}
          columnSpacing="4px"
        />
      )}
      <SegmentedList
        items={[
          {
            key: 'roomtype',
            element: (
              <LabelXSmall color="contentSecondary">{roomType}</LabelXSmall>
            ),
          },
          {
            key: 'boardtype',
            element: (
              <LabelXSmall color="contentSecondary">{boardType}</LabelXSmall>
            ),
          },
        ]}
        category="secondary"
        variant="solid"
        alignment={alignment}
        columnSpacing="4px"
      />
    </Stack>
  );

  const hotelBoxToRender =
    smDown || lgUp ? HotelOfferInfo2Split3 : HotelOfferInfo2Split3ForTablet;

  return isHotelBox2Split3Enabled ? hotelBoxToRender : HotelOfferInfoOriginal;
};
